import { createContext, useCallback, useState, type Dispatch, type SetStateAction, type ReactNode } from 'react';
import {
  DEFAULT_NOISE_SOURCE_DISTANCE,
  DEFAULT_LINE_SOURCE_SPACING_RATIO,
  DEFAULT_MAX_REFLECTED_SOURCE_DISTANCE,
  DEFAULT_NUMBER_OF_REFLECTIONS,
} from 'constants/simulation';
import type { WeatherStation } from './ClosestStationContext';

export type SubSimulationParams = Record<string, unknown>;

export interface SubSimulation {
  id: number;
  name?: string;
  simType?: string;
  type?: string;
  params: SubSimulationParams;
  status?: string;
}

export interface SimulationState {
  name: string;
  description: string;
  createdAt?: string | number | Date | null;
  createdBy?: string | null;
  subSimulations: SubSimulation[];
  useStandardSettings: boolean;
  nameStepEnabled: boolean;
  solarEnabled: boolean;
  freeViewEnabled: boolean;
  targetViewEnabled: boolean;
  noiseEnabled: boolean;
  windEnabled: boolean;
  shadowEnabled: boolean;
  reviewStepEnabled: boolean;
}

export interface TargetViewParams {
  enabled: boolean;
  desiredCellCount: number;
  targetIds: number[];
  targets: unknown[];
  [key: string]: unknown;
}

export type CloneSubSimulationSource = {
  id?: number;
  name?: string | null;
  simType?: string | null;
  type?: string;
  params?: SubSimulationParams | null;
  status?: string | null;
  [key: string]: unknown;
};

export type CloneSimulationSource = {
  name?: string | null;
  description?: string | null;
  createdAt?: string | number | Date | null;
  createdBy?: string | null;
  subSimulations?: CloneSubSimulationSource[];
  solarEnabled?: unknown;
  freeViewEnabled?: unknown;
  targetViewEnabled?: unknown;
  noiseEnabled?: unknown;
  windEnabled?: unknown;
  shadowEnabled?: unknown;
  [key: string]: unknown;
};

export type CloneSimulationOptions = {
  appendClonedSuffix?: boolean;
};

export const expandClonedShadowSubSimulations = (
  subSimulations: CloneSubSimulationSource[]
): CloneSubSimulationSource[] => {
  let nextId = Math.max(-1, ...subSimulations.map((subSimulation) => Number(subSimulation.id ?? -1))) + 1;

  return subSimulations.flatMap((subSimulation) => {
    if (String(subSimulation.simType).toUpperCase() !== 'SHADOW') return [subSimulation];

    const params = subSimulation.params ?? {};
    const volumes = Array.isArray(params.volumes) ? (params.volumes as Record<string, unknown>[]) : [];
    if (volumes.length === 0) return [subSimulation];

    const { volumes: _volumes, ...sharedParams } = params;
    return volumes.map((volume, volumeIndex) => ({
      ...subSimulation,
      id: volumeIndex === 0 ? subSimulation.id : nextId++,
      params: {
        ...sharedParams,
        analysisAreaIndex: Number(volume.analysisAreaIndex ?? volumeIndex),
        normalBuildableArea: Array.isArray(volume.normalArea) ? volume.normalArea : [],
        normalMaximalHeight: Number(volume.normalMaximalHeight ?? 9),
        residentialAreas: Array.isArray(volume.buildableResidentialAreas)
          ? (volume.buildableResidentialAreas as Record<string, unknown>[]).map((area, areaIndex) => ({
              id: areaIndex + 1,
              minElevation: Number(area.minElevation ?? 0),
              maxElevation: Number(area.maxElevation ?? 10),
              coordinates: Array.isArray(area.coordinates) ? area.coordinates : [],
            }))
          : [],
      },
    }));
  });
};

export interface SimulationContextValue {
  simulation: SimulationState;
  setSimulation: Dispatch<SetStateAction<SimulationState>>;
  subSimulations: SubSimulation[];
  setSubSimulations: (subSimulations: SubSimulation[]) => void;
  setSubSimulationParams: (id: number, subSimulationParams: SubSimulationParams) => void;
  clearSimulationFormData: () => void;
  setSubSimulationName: (newName: string, id: number) => void;
  defaultSolar: (newId: number) => SubSimulation;
  defaultFreeview: (newId: number) => SubSimulation;
  defaultNoise: (newId: number) => SubSimulation;
  defaultWind: (newId: number, closestStation: WeatherStation | null, meshDistance?: number | null) => SubSimulation;
  defaultShadow: (newId: number) => SubSimulation;
  cloneSimulationData: (pItem?: CloneSimulationSource, options?: CloneSimulationOptions) => void;
  isCloningSimulation: boolean;
  setIsCloningSimulation: Dispatch<SetStateAction<boolean>>;
}

export const SimulationContext = createContext<SimulationContextValue>({} as SimulationContextValue);

const simulationData: SimulationState = {
  name: '',
  description: '',
  createdAt: null,
  createdBy: null,

  subSimulations: [
    {
      id: 0,
      name: 'Yearly Sun',
      simType: 'SUN',
      type: 'subsimulation',
      params: {
        frameDivision: 'YEARLY',
        raysEveryNMonths: 1,
        raysEveryNDays: 2,
        raysEveryNHours: 1,
        raysEveryNMinutes: 30,
      },
    },
    {
      id: 1,
      name: 'Monthly Sun',
      simType: 'SUN',
      type: 'subsimulation',

      params: {
        frameDivision: 'MONTHLY',
        raysEveryNMonths: 1,
        raysEveryNDays: 5,
        raysEveryNHours: 1,
        raysEveryNMinutes: 10,
      },
    },
    {
      id: 2,
      name: 'Hourly Sun',
      simType: 'SUN',
      type: 'subsimulation',

      params: {
        frameDivision: 'HOURLY',
        raysEveryNMonths: 1,
        raysEveryNDays: 5,
        raysEveryNHours: 1,
        raysEveryNMinutes: 10,
      },
    },
    {
      id: 3,
      name: 'Free View 360 2D',
      simType: 'FREEVIEW',
      type: 'subsimulation',
      params: {
        decompose: true,
        raysLength: 100,
        raysResFactor: 50,
        outward: false,
        angUp: 0,
        angDown: 0,
      },
    },
    {
      id: 4,
      name: 'Free View 180 2D',
      simType: 'FREEVIEW',
      type: 'subsimulation',
      params: {
        decompose: true,
        raysLength: 100,
        raysResFactor: 50,
        outward: true,
        angUp: 0,
        angDown: 0,
      },
    },
    {
      id: 9,
      simType: 'NOISE',
      type: 'subsimulation',
      name: 'Traffic Noise',
      params: {
        numberOfReflection: DEFAULT_NUMBER_OF_REFLECTIONS,
        maxReflectionDistance: DEFAULT_MAX_REFLECTED_SOURCE_DISTANCE,
        maxSourceDistance: DEFAULT_NOISE_SOURCE_DISTANCE,
        lineSourceSpacingRatio: DEFAULT_LINE_SOURCE_SPACING_RATIO,
        horizontalDiffraction: 0,
        verticalDiffraction: 0,
        railways: false,
        filters: [],
      },
    },
    {
      id: 10,
      simType: 'WIND',
      type: 'subsimulation',
      name: 'Main Wind Pattern',
      params: {
        meshDistance: null,
        referenceVelocity: null,
        referenceHeight: null,
        flowDir: null,
        surfaceRoughness: 0.4,
        windAverageBuildingHeight: 0.0,
        windSimulationAccuracy: 'MEDIUM',
      },
    },
  ],

  useStandardSettings: true,
  nameStepEnabled: true,
  solarEnabled: false,
  freeViewEnabled: false,
  targetViewEnabled: false,
  noiseEnabled: false,
  windEnabled: false,
  shadowEnabled: false,
  reviewStepEnabled: true,
};

interface SimulationContextProviderProps {
  children: ReactNode;
}

const SimulationContextProvider = (props: SimulationContextProviderProps) => {
  const [isCloningSimulation, setIsCloningSimulation] = useState(false);
  const [simulation, setSimulation] = useState<SimulationState>(simulationData);

  const toTargetIdsFromUnknownTargets = (targets: unknown[]): number[] =>
    targets
      .map((target: unknown) => {
        if (typeof target === 'number' || typeof target === 'string') {
          const value = Number(target);
          return Number.isFinite(value) ? value : null;
        }

        const targetId = Number((target as { id?: unknown })?.id);
        return Number.isFinite(targetId) ? targetId : null;
      })
      .filter((value): value is number => Number.isFinite(value));

  const normalizeTargetViewParams = (
    params: Record<string, unknown> = {},
    fallbackTargetId?: number
  ): TargetViewParams => {
    const inlineTarget =
      typeof params?.type === 'string' && typeof params?.name === 'string'
        ? [
            {
              id: fallbackTargetId,
              type: params.type,
              name: params.name,
              radius: params.radius ?? null,
              minArea: params.minArea ?? null,
              polygon: params.polygon ?? null,
            },
          ]
        : [];
    const targets = Array.isArray(params?.targets) ? params.targets : inlineTarget;
    const targetIds = Array.isArray(params?.targetIds)
      ? params.targetIds.map((value: unknown) => Number(value)).filter((value: number) => Number.isFinite(value))
      : toTargetIdsFromUnknownTargets(targets);

    return {
      ...params,
      enabled: (params?.enabled as boolean) ?? true,
      desiredCellCount: Number.isFinite(Number(params?.desiredCellCount)) ? Number(params.desiredCellCount) : 2000,
      targetIds,
      targets,
    };
  };

  const clearSimulationFormData = () => {
    setSimulation(simulationData);
  };

  const setSubSimulations = (subSimulations: SubSimulation[]) => {
    setSimulation((prev) => ({
      ...prev,
      subSimulations,
    }));
  };

  // const setSubSimulationName = (newName, id) => {
  //   setSimulation((prev) => {
  //     let finalName = newName;
  //     let count = 1;

  //     while (prev.subSimulations.some((subSim) => subSim.name === finalName && subSim.id !== id)) {
  //       count++;
  //       finalName = `${newName} ${count}`;
  //     }

  //     return {
  //       ...prev,
  //       subSimulations: prev.subSimulations.map((subSim) => (subSim.id === id ? { ...subSim, name: finalName } : subSim)),
  //     };
  //   });
  // };

  const setSubSimulationName = (newName: string, id: number) => {
    setSimulation((prev) => ({
      ...prev,
      subSimulations: prev.subSimulations.map((subSim) => (subSim.id === id ? { ...subSim, name: newName } : subSim)),
    }));
  };

  const setSubSimulationParams = (id: number, subSimulationParams: SubSimulationParams) => {
    setSimulation((prev) => ({
      ...prev,
      subSimulations: prev.subSimulations.map((subSim) =>
        subSim.id === id ? { ...subSim, params: { ...subSim.params, ...subSimulationParams } } : subSim
      ),
    }));
  };

  const defaultSolar = (newId: number): SubSimulation => {
    return {
      id: newId,
      name: 'Yearly Sun',
      simType: 'SUN',
      params: {
        frameDivision: 'YEARLY',
        raysEveryNMonths: 1,
        raysEveryNDays: 2,
        raysEveryNHours: 1,
        raysEveryNMinutes: 30,
      },
    };
  };
  const defaultFreeview = (newId: number): SubSimulation => {
    return {
      id: newId,
      name: 'Free View 360 2D',
      simType: 'FREEVIEW',
      params: {
        decompose: true,
        raysLength: 100,
        raysResFactor: 50,
        outward: false,
        angUp: 0,
        angDown: 0,
      },
    };
  };

  const defaultNoise = (newId: number): SubSimulation => {
    return {
      id: newId,
      simType: 'NOISE',
      name: 'Traffic Noise',
      params: {
        numberOfReflection: DEFAULT_NUMBER_OF_REFLECTIONS,
        maxReflectionDistance: DEFAULT_MAX_REFLECTED_SOURCE_DISTANCE,
        maxSourceDistance: DEFAULT_NOISE_SOURCE_DISTANCE,
        lineSourceSpacingRatio: DEFAULT_LINE_SOURCE_SPACING_RATIO,
        horizontalDiffraction: 0,
        verticalDiffraction: 0,
        railways: false,
        filters: [],
      },
    };
  };

  const defaultWind = (
    newId: number,
    closestStation: WeatherStation | null,
    meshDistance: number | null = null
  ): SubSimulation => {
    return {
      id: newId,
      simType: 'WIND',
      name: 'Main Wind Pattern',
      params: {
        meshDistance,
        referenceVelocity: closestStation ? closestStation.avgYear : 0,
        referenceHeight: closestStation ? closestStation.measurementHeight : 0,
        flowDir: closestStation ? closestStation.avgDir : 0,
        surfaceRoughness: 0.4,
        windAverageBuildingHeight: 0.0,
        windSimulationAccuracy: 'MEDIUM',
      },
    };
  };

  const defaultShadow = (newId: number): SubSimulation => ({
    id: newId,
    simType: 'SHADOW',
    name: 'Shadow Affecting Neighbors',
    params: {
      shadowRule: 'ZH',
      normalMaximalHeight: 9,
      residentialAreas: [{ id: 1, minElevation: 0, maxElevation: 10 }],
    },
  });

  const cloneSimulationData = useCallback((pItem: CloneSimulationSource = {}, options: CloneSimulationOptions = {}) => {
    const { appendClonedSuffix = true } = options;
    const subSims = expandClonedShadowSubSimulations(
      (pItem?.subSimulations ?? []).filter(
        (subSim) => !['SAVED', 'MIXED'].includes(String(subSim?.simType ?? '').toUpperCase())
      )
    );
    const hasSimType = (type: string) => subSims.some((subSim) => subSim?.simType === type);
    const resolveFlag = (value: unknown, fallback: boolean) =>
      typeof value === 'undefined' ? fallback : Boolean(value);
    const clonedSimulationData: SimulationState = {
      name: pItem?.name ? `${pItem.name}${appendClonedSuffix ? ' cloned' : ''}` : '',
      description: pItem?.description || '',
      createdAt: pItem?.createdAt ?? null,
      createdBy: pItem?.createdBy ?? null,
      useStandardSettings: false,
      nameStepEnabled: true,
      solarEnabled: resolveFlag(pItem?.solarEnabled, hasSimType('SUN')),
      freeViewEnabled: resolveFlag(pItem?.freeViewEnabled, hasSimType('FREEVIEW')),
      targetViewEnabled: resolveFlag(pItem?.targetViewEnabled, hasSimType('TARGETVIEW')),
      noiseEnabled: resolveFlag(pItem?.noiseEnabled, hasSimType('NOISE')),
      windEnabled: resolveFlag(pItem?.windEnabled, hasSimType('WIND')),
      shadowEnabled: resolveFlag(pItem?.shadowEnabled, hasSimType('SHADOW')),
      reviewStepEnabled: true,
      subSimulations: subSims.map((subSim) => ({
        id: subSim.id ?? 0,
        name: subSim.name || '',
        simType: subSim.simType || '',
        type: subSim.type || 'subsimulation',
        params:
          subSim.simType === 'TARGETVIEW'
            ? normalizeTargetViewParams(subSim.params ?? {}, Number(subSim.id ?? 0))
            : { ...subSim.params },
      })),
    };

    setSimulation(clonedSimulationData);
  }, []);

  return (
    <SimulationContext.Provider
      value={{
        simulation,
        setSimulation,
        subSimulations: simulation.subSimulations,
        setSubSimulations,
        setSubSimulationParams,
        clearSimulationFormData,
        setSubSimulationName,
        defaultSolar,
        defaultFreeview,
        defaultNoise,
        defaultWind,
        defaultShadow,
        cloneSimulationData,
        isCloningSimulation,
        setIsCloningSimulation,
      }}
    >
      {props.children}
    </SimulationContext.Provider>
  );
};

export default SimulationContextProvider;
